import { useNavigate } from 'react-router-dom';
import { getAllDemoCompanies } from '@/data/demoCompanyBranding';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Building2, Monitor, Smartphone, ChevronLeft } from 'lucide-react';

export default function DemoIndex() {
  const navigate = useNavigate();
  const companies = getAllDemoCompanies();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/10"
            onClick={() => navigate('/')}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back to MEDD Sim
          </Button>
          <Badge variant="outline" className="text-white border-white/30">
            Enterprise Demo Gallery
          </Badge>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-16 text-center">
        <div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-2 rounded-full mb-6">
          <Building2 className="h-4 w-4" />
          <span className="text-sm font-medium">White-Label Platform Demos</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Experience Enterprise White-Label
        </h1>
        <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
          See how leading companies can have their own fully branded training platform 
          powered by MEDD Sim's enterprise infrastructure.
        </p>
        <div className="flex items-center justify-center gap-4 text-gray-500">
          <div className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            <span>Web Platform</span>
          </div>
          <span>•</span>
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            <span>Mobile App</span>
          </div>
        </div>
      </section>

      {/* Company Cards */}
      <section className="container mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-3 gap-8">
          {companies.map((company) => (
            <Card 
              key={company.slug}
              className="bg-white/5 border-white/10 hover:bg-white/10 transition-all duration-300 group overflow-hidden"
            >
              <CardHeader className="pb-4">
                <div 
                  className="h-24 rounded-lg flex items-center justify-center mb-4 transition-transform group-hover:scale-105"
                  style={{ backgroundColor: `hsl(${company.primaryColor} / 0.15)` }}
                >
                  <img 
                    src={company.logoUrl}
                    alt={company.name}
                    className="h-10 w-auto object-contain filter brightness-0 invert"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).parentElement!.innerHTML = `<span class="text-white text-xl font-bold">${company.name}</span>`;
                    }}
                  />
                </div>
                <CardTitle className="text-white text-xl">{company.name}</CardTitle>
                <CardDescription className="text-gray-400">
                  {company.tagline}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{company.sampleStats.teamMembers}</p>
                    <p className="text-gray-500 text-xs">Team Members</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{company.sampleStats.avgScore}%</p>
                    <p className="text-gray-500 text-xs">Avg Score</p>
                  </div>
                </div>
                <Button 
                  className="w-full text-white"
                  style={{ backgroundColor: `hsl(${company.primaryColor})` }}
                  onClick={() => navigate(`/demo/${company.slug}`)}
                >
                  View Demo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="border-t border-white/10">
        <div className="container mx-auto px-6 py-12 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">
            Ready to create your own branded platform?
          </h2>
          <p className="text-gray-400 mb-6">
            Contact our enterprise team to discuss white-label options for your organization.
          </p>
          <Button 
            size="lg"
            className="bg-primary hover:bg-primary/90"
            onClick={() => window.location.href = 'mailto:enterprise@medd.com.au'}
          >
            Contact Enterprise Sales
          </Button>
        </div>
      </section>
    </div>
  );
}
